import { Button } from '@repo/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@repo/ui/dialog'
import { useT } from '@repo/ui/hooks'
import { Input } from '@repo/ui/input'
import { Label } from '@repo/ui/label'
import { ScrollArea } from '@repo/ui/scrollArea'
import { Switch } from '@repo/ui/switch'
import { onInput, sortByKey } from '@repo/ui/utils'
import { FileDownIcon, SearchIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { Section } from '@/components/tasks/TaskList'
import { exportBeneficiariesToCsv } from '@/store/beneficiary-list-filters/thunks'
import { useStoreDispatch } from '@/store/hooks'

type Column = { label: string; id: string; selected: boolean }

export function CsvExporter({
  initialColumns,
  disabled,
}: {
  initialColumns: Column[]
  disabled: boolean
}) {
  const dispatch = useStoreDispatch()
  const { t, tc } = useT({ keyPrefix: 'beneficiaryList.csvExporter' })
  const [searchTerm, setSearchTerm] = useState('')
  const [columns, setColumns] = useState<Column[]>([])
  const [open, setOpen] = useState(false)
  const [exporting, setExporting] = useState(false)

  const selected = columns.filter((col) => col.selected)
  const unselected = columns.filter((col) => !col.selected)
  const filteredSelected = selected.filter((column) =>
    column.label.toLowerCase().includes(searchTerm.toLowerCase()),
  )
  const filteredUnselected = unselected.filter((column) =>
    column.label.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  const handleCheckboxChange = (columnId: string) => {
    setColumns((prev) =>
      prev.map((column) =>
        column.id === columnId ? { ...column, selected: !column.selected } : column,
      ),
    )
  }

  const handleExport = async () => {
    setExporting(true)
    const { csv } = await dispatch(
      exportBeneficiariesToCsv({ columnIds: selected.map((col) => col.id) }),
    ).unwrap()

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `beneficiaries-export-${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    setExporting(false)
  }

  const isDisabled = disabled || columns.every((column) => !column.selected) || exporting

  useEffect(() => {
    setColumns(initialColumns.sort(sortByKey('label')))
  }, [initialColumns])
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" disabled={disabled}>
          <FileDownIcon className="size-4" /> {t('export')}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
          <DialogDescription>{t('description')}</DialogDescription>
        </DialogHeader>

        <div className="m-4">
          <SearchInput value={searchTerm} onChange={setSearchTerm} />
        </div>
        <ScrollArea className="max-h-96 px-4">
          {filteredSelected.length > 0 && (
            <Section
              title={tc('selectedColumn', {
                cfl: true,
                count: filteredSelected.length,
              })}
            >
              {filteredSelected.map((column) => (
                <div
                  className="hover:bg-primary-50 flex items-center gap-4 px-4 py-2 transition-colors border rounded-lg border-primary-50 bg-white"
                  key={column.id}
                >
                  <Label className="flex capitalize items-center gap-1 flex-1 text-base select-none">
                    {column.label}
                  </Label>

                  <Switch
                    checked={column.selected}
                    onCheckedChange={() => handleCheckboxChange(column.id)}
                  />
                </div>
              ))}
            </Section>
          )}

          {filteredUnselected.length > 0 && (
            <Section
              className="mt-6"
              title={tc('unselectedColumn', {
                cfl: true,
                count: filteredUnselected.length,
              })}
            >
              {filteredUnselected.map((column) => (
                <div
                  className="hover:bg-primary-50 flex items-center gap-4 px-4 py-2 transition-colors border rounded-lg border-primary-50 bg-white"
                  key={column.id}
                >
                  <Label className="flex capitalize items-center gap-1 flex-1 text-base select-none">
                    {column.label}
                  </Label>

                  <Switch
                    checked={column.selected}
                    onCheckedChange={() => handleCheckboxChange(column.id)}
                  />
                </div>
              ))}
            </Section>
          )}
        </ScrollArea>
        <DialogFooter>
          <Button disabled={isDisabled} className="w-full" onClick={() => void handleExport()}>
            {t('export')}
            {!isDisabled && (
              <span>({tc('selectedColumn', { cfl: true, count: selected.length })})</span>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function SearchInput({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const { tc } = useT()
  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    inputRef.current?.focus()
  }, [value])
  return (
    <div className="relative">
      <SearchIcon className="absolute left-2 top-1/2 size-4 -translate-y-1/2 opacity-50" />
      <Input
        ref={inputRef}
        value={value}
        onChange={onInput(onChange)}
        placeholder={tc('search', { cfl: true }) + '...'}
        className="w-full pl-8"
        type="search"
      />
    </div>
  )
}
